import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  TextField,
  Grid,
  Paper
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import PINValidator from './PINValidator';
import {
  generateAndImportCertificate,
  validateSubjectDN
} from './certificateGenerator';

/**
 * Certificate Generation Dialog for PIV
 * Generates self-signed X.509 certificates for PIV keys
 */

const CertificateGenerationDialog = ({ open, onClose, slot, slotName, keyData, dispatch, onCertificateGenerated }) => {
  const [subject, setSubject] = useState({
    CN: '',
    O: '',
    OU: '',
    C: '',
    ST: '',
    L: ''
  });
  const [validityDays, setValidityDays] = useState(365);
  const [pin, setPin] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [certificateInfo, setCertificateInfo] = useState(null);

  const handleSubjectChange = (field, value) => {
    setSubject(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleGenerate = async () => {
    // Validate subject DN
    const validation = validateSubjectDN(subject);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    if (!pin) {
      setError('Please enter your PIN');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setError('');

    try {
      setProgress(20);

      // Generate and import certificate
      const result = await generateAndImportCertificate(
        dispatch,
        slot,
        subject,
        keyData,
        pin,
        parseInt(validityDays)
      );

      setProgress(100);

      if (result.success) {
        setSuccess(true);
        setCertificateInfo(result);

        if (onCertificateGenerated) {
          onCertificateGenerated(slot, result);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Certificate generation error:', error);
      setError(error.message || 'Certificate generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (!generating) {
      setSubject({ CN: '', O: '', OU: '', C: '', ST: '', L: '' });
      setValidityDays(365);
      setPin('');
      setError('');
      setSuccess(false);
      setProgress(0);
      setCertificateInfo(null);
      onClose();
    }
  };

  const handleDownloadCertificate = () => {
    if (certificateInfo && certificateInfo.certificate) {
      const blob = new Blob([certificateInfo.certificate], { type: 'application/x-pem-file' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `piv-slot-${slot}-certificate.pem`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={generating}
    >
      <DialogTitle>
        Generate Certificate for Slot {slot}
      </DialogTitle>
      <DialogContent>
        {!success ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Self-Signed Certificate</AlertTitle>
              Generate a self-signed X.509 certificate for {slotName}. Fill in your identity information below.
            </Alert>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Certificate Validity</AlertTitle>
              Self-signed certificates are suitable for testing and personal use.
              For production use, consider getting a certificate from a trusted Certificate Authority (CA).
            </Alert>

            {/* Subject DN Fields */}
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#f9f9f9' }}>
              <Typography variant="subtitle2" gutterBottom>
                Certificate Subject Information:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Common Name (CN)"
                    value={subject.CN}
                    onChange={(e) => handleSubjectChange('CN', e.target.value)}
                    helperText="Your name or hostname (e.g., John Doe)"
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Organization (O)"
                    value={subject.O}
                    onChange={(e) => handleSubjectChange('O', e.target.value)}
                    helperText="Company name"
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Organizational Unit (OU)"
                    value={subject.OU}
                    onChange={(e) => handleSubjectChange('OU', e.target.value)}
                    helperText="Department"
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Country (C)"
                    value={subject.C}
                    onChange={(e) => handleSubjectChange('C', e.target.value.toUpperCase())}
                    helperText="2-letter code"
                    inputProps={{ maxLength: 2 }}
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="State (ST)"
                    value={subject.ST}
                    onChange={(e) => handleSubjectChange('ST', e.target.value)}
                    helperText="State/Province"
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City (L)"
                    value={subject.L}
                    onChange={(e) => handleSubjectChange('L', e.target.value)}
                    helperText="City/Locality"
                    disabled={generating}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Validity (days)"
                    value={validityDays}
                    onChange={(e) => setValidityDays(e.target.value)}
                    helperText="Certificate will be valid for this many days"
                    inputProps={{ min: 1, max: 3650 }}
                    disabled={generating}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* PIN Input */}
            <PINValidator
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              label="PIV PIN"
              type="PIV"
              dispatch={dispatch}
              disabled={generating}
            />

            {/* Progress Bar */}
            {generating && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                  Generating certificate...
                </Typography>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
                <AlertTitle>Generation Failed</AlertTitle>
                {error}
              </Alert>
            )}
          </>
        ) : (
          /* Success Display */
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Certificate Generated Successfully!
            </Typography>

            {certificateInfo && (
              <Paper elevation={2} sx={{ p: 2, mt: 3, textAlign: 'left' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Certificate Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Subject:</strong> CN={subject.CN}
                  {subject.O && `, O=${subject.O}`}
                  {subject.C && `, C=${subject.C}`}
                </Typography>
                <Typography variant="body2">
                  <strong>Valid Until:</strong> {new Date(certificateInfo.validUntil).toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Slot:</strong> {slot} ({slotName})
                </Typography>
              </Paper>
            )}

            <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
              <AlertTitle>Next Steps</AlertTitle>
              <Typography variant="body2">
                • Download the certificate for backup<br />
                • The certificate is now stored on your card<br />
                • You can use this key for authentication, signing, or encryption
              </Typography>
            </Alert>

            <Button
              variant="outlined"
              onClick={handleDownloadCertificate}
              sx={{ mt: 2 }}
              fullWidth
            >
              Download Certificate (PEM)
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!success ? (
          <>
            <Button onClick={handleClose} disabled={generating}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              variant="contained"
              disabled={!subject.CN || !pin || generating}
            >
              {generating ? 'Generating...' : 'Generate Certificate'}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CertificateGenerationDialog;
